import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { lenisStore, EASE } from "@/lib/lenis";
import { MOCKUP_LINK, NAV_LINKS, SITE } from "@/content/site";
import { Wordmark } from "@/components/site/Wordmark";
import { CTAButton } from "@/components/site/CTAButton";

const slug = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-");

const MobileMenu = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        id="mobile-menu"
        key="mobile-menu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-40 flex h-[100dvh] flex-col bg-[#0A0B0D] px-5 pb-8 pt-20 md:hidden"
        data-testid="mobile-menu"
      >
        <nav aria-label="Mobile" className="flex-1">
          <ul className="border-t border-white/10">
            {[{ to: "/", label: "Home" }, ...NAV_LINKS].map((link, i) => (
              <motion.li
                key={link.to}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.05 + i * 0.05 }}
                className="border-b border-white/10"
              >
                <NavLink
                  to={link.to}
                  end
                  onClick={onClose}
                  data-testid={`mobile-nav-${slug(link.label)}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center justify-between py-5 text-3xl font-medium tracking-[-0.03em]",
                      isActive ? "text-white" : "text-white/70",
                    )
                  }
                >
                  {link.label}
                  <ArrowUpRight className="h-6 w-6 text-white/40" aria-hidden="true" />
                </NavLink>
              </motion.li>
            ))}
          </ul>
        </nav>
        <div className="space-y-4">
          <CTAButton to={MOCKUP_LINK} size="lg" className="w-full" onClick={onClose} testId="mobile-menu-cta-button">
            Get a free mockup
          </CTAButton>
          <a href={`mailto:${SITE.email}`} className="block text-center text-sm text-[#A1A1AA]" data-testid="mobile-menu-email">
            {SITE.email}
          </a>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const lenis = lenisStore.current;
    if (open) lenis?.stop();
    else lenis?.start();
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={cn(
          "fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-300",
          scrolled || open ? "border-white/[0.08] bg-[#0A0B0D]/80 backdrop-blur-xl" : "border-transparent bg-transparent",
        )}
      >
        <nav aria-label="Main" className="container-x flex h-16 items-center justify-between">
          <Wordmark />
          <ul className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  data-testid={`nav-link-${slug(link.label)}`}
                  className={({ isActive }) =>
                    cn(
                      "rounded-full px-3.5 py-2 text-sm transition-colors duration-200",
                      isActive ? "text-white" : "text-[#A1A1AA] hover:text-white",
                    )
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <CTAButton to={MOCKUP_LINK} size="sm" arrow={false} className="hidden sm:inline-flex" testId="nav-cta-button">
              Get a free mockup
            </CTAButton>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white transition-colors hover:bg-white/[0.06] md:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </header>
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
};
